import type { RatingSummary } from '@shared/domain/review';
import type {
  CurrentReadableReview,
  ReviewAnswer,
  ReviewSection,
} from '../types/types';

interface LabelMeta {
  label: string;
  iconGroup: string;
  sortOrder: number;
  section: string;
}

type LabelMap = Record<string, LabelMeta>;

export function formatReviewData(reviewData: any): CurrentReadableReview {
  const labels: LabelMap = reviewData.labels;

  type SectionKey = keyof Pick<
    CurrentReadableReview,
    | 'reviewEvaluation'
    | 'reviewEntryStrategy'
    | 'reviewLegal'
    | 'reviewNegotiation'
    | 'reviewPurchaseDetails'
    | 'reviewRfp'
  >;

  const reviewSections: SectionKey[] = [
    'reviewEvaluation',
    'reviewEntryStrategy',
    'reviewLegal',
    'reviewNegotiation',
    'reviewPurchaseDetails',
    'reviewRfp',
  ];

  type SectionsMap = Partial<Record<SectionKey, ReviewSection>>;
  const formattedSections: SectionsMap = {};

  reviewSections.forEach((sectionKey) => {
    if (reviewData[sectionKey] && typeof reviewData[sectionKey] === 'object') {
      const sectionEntries = Object.entries(reviewData[sectionKey])
        .filter(([key]) => labels[key]) // only include if label exists
        .sort((a, b) => {
          const aOrder = labels[a[0]]?.sortOrder ?? Infinity;
          const bOrder = labels[b[0]]?.sortOrder ?? Infinity;
          return aOrder - bOrder;
        });

      formattedSections[sectionKey] = sectionEntries.reduce<ReviewSection>(
        (acc, [key, value]) => {
          const meta = labels[key];
          acc[key] = {
            label: meta.label,
            iconGroup: meta.iconGroup,
            answer: Array.isArray(value)
              ? value
              : typeof value === 'string' || typeof value === 'number'
                ? value
                : null,
          };
          return acc;
        },
        {} as ReviewSection,
      );
    }
  });

  const excludedKeys = [...reviewSections, 'labels', 'ratings'];

  const basicReview: ReviewSection = Object.entries(
    reviewData,
  ).reduce<ReviewSection>((acc, [key, value]) => {
    if (!excludedKeys.includes(key) && labels[key]) {
      acc[key] = {
        label: labels[key].label,
        iconGroup: labels[key].iconGroup,
        answer: Array.isArray(value)
          ? value
          : typeof value === 'string' || typeof value === 'number'
            ? value
            : null,
      };
    }
    return acc;
  }, {});

  if (reviewData.ratings) {
    basicReview.ratings = reviewData.ratings.map(
      (rating: RatingSummary): ReviewAnswer => ({
        label: rating.type,
        iconGroup: labels[rating.type]?.iconGroup || 'default',
        answer: rating.rating,
      }),
    );
  }

  return {
    companyName: reviewData.companyName || '',
    companyId: reviewData.companyId || 0,
    createdAt: reviewData.createdAt || '',
    reviewBasic: basicReview,
    id: reviewData.id || 0,
    ...(formattedSections as Partial<CurrentReadableReview>),
  };
}
